import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useAppConfig } from "@/contexts/AppConfigContext";
import {
  FileText, Eye, EyeOff, Clock, Zap, BarChart2,
  Layers, Webhook, ShieldCheck, ArrowRight
} from "lucide-react";
import api from "@/api";

// ── Feature slides — one shown per page visit ─────────────────────────────────
const SLIDES = [
  {
    tag: "Intelligent Extraction",
    pain: "Manual data entry from documents is costing you hours every single day.",
    solution:
      "Train a schema once. DocIntel's AI reads, understands, and extracts every field — from any document format — in seconds.",
    icon: Zap,
    stat: "10×",
    statLabel: "faster than manual entry",
    accent: "#00B4D8",
  },
  {
    tag: "Multi-Format OCR",
    pain: "Different vendors. Different formats. Same repetitive nightmare.",
    solution:
      "Invoices, contracts, IDs, receipts — one platform handles them all. Configure once, extract forever with Mistral AI.",
    icon: FileText,
    stat: "Any",
    statLabel: "document type supported",
    accent: "#06D6A0",
  },
  {
    tag: "Bulk Processing",
    pain: "You need structured data from 200 documents by tomorrow morning.",
    solution:
      "Upload in bulk, extract in parallel, download a clean CSV instantly. No manual work. No waiting.",
    icon: Layers,
    stat: "200+",
    statLabel: "documents in one batch job",
    accent: "#FFB703",
  },
  {
    tag: "Webhooks & APIs",
    pain: "Extracted data trapped in a dashboard nobody reads.",
    solution:
      "Fire results directly to your CRM, ERP, or any endpoint the moment extraction completes. Your workflow, automated.",
    icon: Webhook,
    stat: "Real-time",
    statLabel: "delivery via webhooks",
    accent: "#FB5607",
  },
  {
    tag: "Multi-Tenant Platform",
    pain: "Managing document workflows across multiple clients is a full-time job.",
    solution:
      "Isolated companies, separate schemas, per-client API keys and credit limits — all from one superadmin panel.",
    icon: ShieldCheck,
    stat: "∞",
    statLabel: "clients on one platform",
    accent: "#C77DFF",
  },
  {
    tag: "Pipelines & Reports",
    pain: "One document, three possible schema matches — and no way to know which won.",
    solution:
      "Group schemas into Pipelines with min-match logic. Track usage, overages, and extraction quality per client.",
    icon: BarChart2,
    stat: "Smart",
    statLabel: "multi-schema matching",
    accent: "#06D6A0",
  },
];

// ── Animated right panel ──────────────────────────────────────────────────────
function FeaturePanel({ slide }) {
  const Icon = slide.icon;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, [slide]);

  return (
    <div
      className="hidden lg:flex flex-1 relative overflow-hidden items-center justify-center p-12"
      style={{
        background: "linear-gradient(145deg, #002D3F 0%, #004B66 55%, #006080 100%)",
      }}
    >
      {/* Dot-grid texture */}
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle, rgba(255,255,255,0.18) 1px, transparent 1px)",
          backgroundSize: "28px 28px",
        }}
      />

      {/* Glowing accent circle — top right */}
      <div
        className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-10 blur-3xl pointer-events-none"
        style={{ background: slide.accent }}
      />
      {/* Glowing accent circle — bottom left */}
      <div
        className="absolute -bottom-24 -left-24 w-64 h-64 rounded-full opacity-10 blur-2xl pointer-events-none"
        style={{ background: slide.accent }}
      />

      {/* Content */}
      <div
        className={`relative z-10 max-w-sm w-full transition-all duration-700 ${
          visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
        }`}
      >
        {/* Tag pill */}
        <div className="inline-flex items-center gap-2 mb-8">
          <span
            className="text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full border"
            style={{
              color: slide.accent,
              borderColor: `${slide.accent}55`,
              background: `${slide.accent}15`,
            }}
          >
            {slide.tag}
          </span>
        </div>

        {/* Icon */}
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center mb-8"
          style={{ background: `${slide.accent}20`, border: `1px solid ${slide.accent}40` }}
        >
          <Icon size={26} style={{ color: slide.accent }} />
        </div>

        {/* Pain point */}
        <h2 className="text-2xl font-bold text-white leading-snug mb-5">
          {slide.pain}
        </h2>

        {/* Divider */}
        <div
          className="w-10 h-0.5 mb-5 rounded-full"
          style={{ background: slide.accent }}
        />

        {/* Solution */}
        <p className="text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.65)" }}>
          {slide.solution}
        </p>

        {/* Stat badge */}
        <div className="flex items-center gap-3 mt-8">
          <div
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl"
            style={{ background: `${slide.accent}18`, border: `1px solid ${slide.accent}35` }}
          >
            <span className="text-xl font-black" style={{ color: slide.accent }}>
              {slide.stat}
            </span>
            <span className="text-xs text-white/50 leading-tight max-w-[110px]">
              {slide.statLabel}
            </span>
          </div>
          <ArrowRight size={14} className="text-white/20" />
        </div>

        {/* Slide dots */}
        <div className="flex items-center gap-1.5 mt-10">
          {SLIDES.map((_, i) => (
            <div
              key={i}
              className="rounded-full transition-all duration-300"
              style={{
                width: i === SLIDES.indexOf(slide) ? 20 : 6,
                height: 6,
                background:
                  i === SLIDES.indexOf(slide)
                    ? slide.accent
                    : "rgba(255,255,255,0.2)",
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Main Login page ───────────────────────────────────────────────────────────
export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [setupComplete, setSetupComplete] = useState(true); // optimistic: hide link until we know
  const { login } = useAuth();
  const navigate = useNavigate();
  const { appName, logoB64 } = useAppConfig();

  // Pick a random slide, fixed for this page visit
  const slide = useMemo(() => SLIDES[Math.floor(Math.random() * SLIDES.length)], []);

  // Check whether first-time setup is still needed
  useEffect(() => {
    api.get("/auth/check-setup")
      .then(r => setSetupComplete(r.data.setup_complete === true))
      .catch(() => setSetupComplete(true)); // if endpoint fails, don't show link
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const user = await login(email, password);
      if (user.role === "superadmin") navigate("/admin");
      else navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#F9FAFB]">
      {/* ── Left: login form ──────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col justify-center px-8 py-12 max-w-md mx-auto w-full">
        {/* Brand */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-6">
            {logoB64 ? (
              <img
                src={`data:image/png;base64,${logoB64}`}
                alt={appName}
                className="h-8 max-w-[140px] object-contain"
              />
            ) : (
              <>
                <div className="w-8 h-8 bg-[#006080] rounded-md flex items-center justify-center">
                  <FileText size={16} className="text-white" />
                </div>
                <span className="font-bold text-slate-900 text-lg">{appName}</span>
              </>
            )}
          </div>
          <h2 className="text-3xl font-bold text-slate-900 tracking-tight">Welcome back</h2>
          <p className="text-sm text-slate-500 mt-1">Sign in to your account to continue</p>
        </div>

        {/* Error */}
        {error && (
          <div
            data-testid="login-error"
            className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700"
          >
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs font-bold tracking-wide uppercase text-slate-500 block mb-1.5">
              Email address
            </label>
            <input
              data-testid="login-email-input"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              className="w-full h-10 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] focus:border-[#006080] bg-white transition-colors"
              placeholder="you@company.com"
            />
          </div>
          <div>
            <label className="text-xs font-bold tracking-wide uppercase text-slate-500 block mb-1.5">
              Password
            </label>
            <div className="relative">
              <input
                data-testid="login-password-input"
                type={showPwd ? "text" : "password"}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                className="w-full h-10 px-3 pr-10 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] focus:border-[#006080] bg-white transition-colors"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPwd(!showPwd)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <button
            data-testid="login-submit-button"
            type="submit"
            disabled={loading}
            className="w-full h-10 bg-[#006080] hover:bg-[#004B66] text-white text-sm font-medium rounded-md transition-colors duration-150 disabled:opacity-50"
          >
            {loading ? "Signing in..." : "Sign in"}
          </button>
        </form>

        {/* First-time setup link — only shown before setup is complete */}
        {!setupComplete && (
          <p className="mt-6 text-xs text-slate-400 text-center">
            First time?{" "}
            <Link to="/setup" className="text-[#006080] hover:underline font-medium">
              Set up your admin account
            </Link>
          </p>
        )}
      </div>

      {/* ── Right: rotating feature panel ────────────────────────────────── */}
      <FeaturePanel slide={slide} />
    </div>
  );
}
